import Vue from "vue";
import VueRouter from "vue-router";
import RouteObjects from "@/shared/router/route-objects";
import AccountHolderStore from "@/nextlevel-account-holder/store";
import {
  authenticatedLandingPath,
  idvRouteForStep,
  isIdVerificationTerminal,
  resolveConfirmIdentityAccess,
  stepAllowedNext,
  TERMINAL_ROUTE,
} from "./routes";

var router = {};

if (window.location.host.match(/nextlevel.*\.penchecks\.com/g) !== null) {
  Vue.use(VueRouter);

  const routes = [
    {
      path: "/",
      name: "Account Holder Landing Page",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcLandingPage.vue"),
    },
    {
      path: "/landing/:prospectUuid([a-zA-Z0-9]{32})?",
      name: "Account Holder Open Account",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcLandingPage.vue"),
    },
    {
      path: "/lead/:partnerProspectSource([a-zA-Z0-9]{32})/:iraPlanId(NL[0-9]{10}|NL-[0-9]{6}-[0-9]{4})/:partnerIraProspectId",
      name: "Partner Prospect Landing",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcLandingPage.vue"),
    },
    {
      path: "/lead/:partnerProspectSource([a-zA-Z0-9]{32})",
      name: "Partner Prospect Landing (No Prospect)",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcLandingPage.vue"),
    },
    {
      path: "/start",
      redirect: "/start/create-account",
    },
    {
      path: "/start/create-account",
      name: "Create Account",
      meta: {
        /* title: "Open a NextLevel IRA Account", */ allowsAnonymousUser: true,
        signupFunnel: true,
      },
      component: () => import("../views/PcCreateAccount.vue"),
    },
    {
      path: "/invite/:prospectUuid([a-zA-Z0-9]{32})?",
      redirect: (to) => `/invite/${to.params.prospectUuid}/create-account`,
    },
    {
      path: "/invite/:prospectUuid([a-zA-Z0-9]{32})/create-account",
      name: "Create Account from Invite",
      meta: { allowsAnonymousUser: true, signupFunnel: true },
      component: () => import("../views/PcCreateAccount.vue"),
    },
    {
      path: "/compare-nextlevel-ira",
      name: "Explore Your 401(k) Options",
      meta: {
        /* title: "Explore Your 401(k) Options", */ allowsAnonymousUser: true,
      },
      component: () => import("../views/PcNliComparisonPage.vue"),
    },
    {
      path: "/open-account",
      name: "Open Account",
      meta: {
        /* title: "Open a NextLevel IRA Account", */ allowsAnonymousUser: false,
      },
      component: () => import("../views/PcOpenNliAccount.vue"),
    },
    {
      path: "/acknowledge",
      name: "Acknowledge Disclosure",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcAcknowledgeDisclosure.vue"),
    },
    {
      path: "/verify-identity",
      name: "Verify Identity",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcVerifyIdentity.vue"),
    },
    {
      path: "/confirm-identity",
      name: "Confirm Identity",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcConfirmIdentity.vue"),
    },
    {
      path: "/contact-support",
      name: "Contact Support",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcContactSupport.vue"),
    },
    {
      path: "/access-removed",
      name: "Access Removed",
      meta: { allowsAnonymousUser: false },
      component: () => import("@/shared/components/PcAccessRemoved.vue"),
    },
    {
      path: "/apply-account",
      name: "Apply Account",
      meta: {
        /* title: "Apply for a NextLevel IRA Account", */ allowsAnonymousUser: false,
      },
      component: () => import("../views/PcApplyNliAccount.vue"),
    },
    {
      path: "/view-account",
      name: "View Account",
      meta: { /* title: "View Account", */ allowsAnonymousUser: false },
      component: () => import("../views/PcViewNliAccount.vue"),
    },
    {
      path: "/account-opening-history",
      name: "Account-Opening History",
      meta: {
        /* title: "Account-Opening History", */ allowsAnonymousUser: false,
      },
      component: () => import("../views/PcAccountOpeningHistory.vue"),
    },
    {
      path: "/transfer-assets",
      name: "Transfer Assets",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcTransferAssets.vue"),
    },
    {
      path: "/set-ira-preferences",
      name: "Set IRA Preferences",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcSetIraPreferences.vue"),
    },
    {
      path: "/ijoin",
      name: "Update IRA Preferences",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcSetIraPreferences.vue"),
    },
    {
      path: "/login",
      name: "Login",
      meta: { /* title: "Login", */ allowsAnonymousUser: true },
      component: () => import("../views/PcLogin.vue"),
    },
    {
      path: "/options",
      name: "Options",
      meta: { /* title: "Options", */ allowsAnonymousUser: false },
      component: () => import("../views/PcNliOptions.vue"),
    },
    {
      path: "/forgot-password",
      name: "Forgot Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcForgotPassword.vue"),
    },
    {
      path: "/reset-password/:email_address/:reset_password_token",
      name: "Reset Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcResetPassword.vue"),
    },
    {
      path: "/confirm-email-address",
      name: "Confirm Email Address",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcConfirmEmailAddress.vue"),
    },
    {
      path: "/prospect-subscription/:prospectUuid([a-zA-Z0-9]{32})/:action(unsubscribe|resubscribe)",
      name: "Manage Prospect Subscription",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcProspectSubscription.vue"),
    },
    {
      path: "/change-email-address/:emailAddress/:token",
      name: "Change Email Address",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcChangeEmailAddress.vue"),
    },
    {
      path: "/my-account",
      name: "My Account",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/create-login",
      name: "My Account - Create Login",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login",
      name: "My Account - Edit Login",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login/change-email-address",
      name: "My Account - Edit login - Change email address",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login/change-password",
      name: "My Account - Edit login - Change password",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-my-information",
      name: "My Account - Edit my information",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-contact",
      name: "My Account - Edit contact",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/home",
      name: "Account Holder Home Page",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcHome.vue"),
    },
    {
      path: "/contribute",
      name: "Contribute to IRA",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcContributeToIra.vue"),
    },
    {
      path: "/activity",
      name: "Activity",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcActivity.vue"),
    },
    {
      path: "/activity/:orderLineUuid([a-zA-Z0-9]{32})",
      name: "Activity Details",
      meta: { allowsAnonymousUser: false },
      component: () => import("../views/PcActivityDetails.vue"),
    },
  ];

  router = new VueRouter({
    mode: "history",
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else {
        return { x: 0, y: 0 };
      }
    },
  });

  router.objects = new RouteObjects(router);

  const IDV_ROUTES = [
    "/verify-identity",
    "/confirm-identity",
    "/contact-support",
  ];

  router.beforeEach((to, from, next) => {
    async function resolveNextRoute() {
      const user = AccountHolderStore.state.authenticatedUser;
      const path = to.path.replace(/\/+$/, "").toLowerCase() || "/";

      if (to.meta.allowsAnonymousUser) {
        if (user && (path === "/login" || path === "/")) {
          return next({ path: authenticatedLandingPath(user), replace: true });
        }
        if (user && to.meta.signupFunnel) {
          return next({ path: authenticatedLandingPath(user), replace: true });
        }
        const prospectUuid = to.query.prospectUuid;
        if (
          prospectUuid &&
          !AccountHolderStore.state.nliraCustomBranding
            ?.effectiveNliraCustomBrand
        ) {
          await AccountHolderStore.dispatch(
            "nliraCustomBranding/fetchEffectiveCustomBrand",
            { prospectUuid: prospectUuid }
          );
        }
        return next();
      }

      if (!user) {
        return next({
          name: "Login",
          query: {
            returnUrl: to.fullPath,
          },
        });
      }

      // Checked before every other authenticated gate, so no route but TERMINAL_ROUTE is reachable
      // once a prospect is declined. TERMINAL_ROUTE is admitted here rather than falling through to
      // the IDV_ROUTES block below, which would bounce an un-acknowledged prospect to /acknowledge —
      // and this gate would bounce it straight back, looping.
      if (isIdVerificationTerminal(user)) {
        if (path === TERMINAL_ROUTE) {
          return next();
        }
        return next({ path: TERMINAL_ROUTE, replace: true });
      }

      if (path === "/home" && !user.isOnboardingCompleted) {
        return next({ path: "/open-account", replace: true });
      }

      if (IDV_ROUTES.includes(path)) {
        // Acknowledgement precedes IDV: an un-acknowledged prospect cannot skip
        // ahead to any IDV route and must complete /acknowledge first.
        if (user.isProspectAcknowledged !== true) {
          return next({ path: "/acknowledge", replace: true });
        }
        if (path === "/confirm-identity") {
          const access = resolveConfirmIdentityAccess(user);
          if (access.action === "next") {
            return next();
          }
          return next({ path: access.path, replace: true });
        }
        const stepRoute = idvRouteForStep(user.currentOnboardingStep);
        const allowedNext = stepAllowedNext[user.currentOnboardingStep] || [];
        const isExplicitlyAllowed = allowedNext.some(
          (rule) =>
            rule.path === path &&
            Object.entries(rule.query || {}).every(
              ([key, value]) => to.query[key] === value
            )
        );
        if (isExplicitlyAllowed) {
          return next();
        }
        if (!stepRoute) {
          return next({ path: authenticatedLandingPath(user), replace: true });
        }
        if (path === stepRoute) {
          return next();
        }
        return next({ path: stepRoute, replace: true });
      }

      // Once the NLI application has been submitted, the acknowledge/apply steps
      // are closed. A submitted prospect belongs on /view-account and must not be
      // able to re-enter /apply-account or /acknowledge (e.g. via a manual URL
      // change). Any redirect toward /apply-account re-enters this guard and is
      // caught here as well.
      if (
        user.hasSubmittedNliApplication === true &&
        (path === "/apply-account" || path === "/acknowledge")
      ) {
        return next({ path: "/view-account", replace: true });
      }

      const ACK_ROUTES = ["/acknowledge", "/apply-account"];
      if (ACK_ROUTES.includes(path)) {
        const isAck = user.isProspectAcknowledged === true;
        if (path === "/apply-account" && !isAck)
          return next({ path: "/acknowledge", replace: true });
        if (path === "/acknowledge" && isAck)
          return next({ path: "/apply-account", replace: true });
      }

      return next();
    }

    if (AccountHolderStore.state.isLoadingAuthenticatedUser) {
      const unwatch = AccountHolderStore.watch(
        (state) => state.isLoadingAuthenticatedUser,
        (loading) => {
          if (!loading) {
            unwatch();
            resolveNextRoute();
          }
        }
      );
    } else {
      resolveNextRoute();
    }
  });

  router.afterEach((to) => {
    const customBrandName =
      AccountHolderStore.state.nliraCustomBranding?.effectiveNliraCustomBrand
        ?.displayName || "NextLevel";

    if (to.meta && to.meta.title) {
      document.title = to.meta.title + " | " + customBrandName + " Portal";
    } else {
      document.title = customBrandName + " Portal";
    }

    if (router.app && router.app.$gtag) {
      router.app.$gtag.pageview({
        page_title: document.title,
        page_path: to.path,
        page_location: window.location.href,
      });
    }
  });
}

export default router;
